import * as RadixDialog from "@radix-ui/react-dialog";
import { cn } from "@/lib/cn";
import { Button } from "./Button";
import { XIcon } from "./icons";
import type { ReactNode } from "react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  size?: "md" | "lg";
  children: ReactNode;
  footer?: ReactNode;
};

/**
 * Lightweight Radix Dialog wrapper. Used by the final-confirmation modal
 * on approval and the zoom-to-print-size preview.
 */
export function Dialog({ open, onOpenChange, title, size = "md", children, footer }: Props) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay
          data-chrome
          className="fixed inset-0 z-50 grid place-items-center bg-[rgba(33,43,50,0.55)] p-10"
        >
          <RadixDialog.Content
            data-chrome
            className={cn(
              "flex max-h-[90vh] w-full flex-col rounded-lg bg-white shadow-2xl",
              size === "lg" ? "max-w-[760px]" : "max-w-[560px]",
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[var(--border-1)] px-5 py-4">
              <RadixDialog.Title className="m-0 text-lg font-semibold text-fg-1">
                {title}
              </RadixDialog.Title>
              <RadixDialog.Close asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Close"
                  className="h-7 w-7 px-0"
                >
                  <XIcon width={16} height={16} />
                </Button>
              </RadixDialog.Close>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5 text-sm leading-relaxed text-fg-1">
              {children}
            </div>
            {footer && (
              <div className="flex justify-end gap-2 border-t border-[var(--border-1)] bg-bg-1 px-5 py-3">
                {footer}
              </div>
            )}
          </RadixDialog.Content>
        </RadixDialog.Overlay>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
